// src/context/LayoutContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';

interface LayoutContextType {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  zoomLevel: number;
  setZoomLevel: (level: number) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize sidebar state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Initialize zoom level from localStorage (default 100%)
  const [zoomLevel, setZoomLevelState] = useState(() => {
    const saved = localStorage.getItem('zoomLevel');
    return saved ? parseInt(saved, 10) : 100;
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed]);

  // Save zoom level to localStorage
  useEffect(() => {
    localStorage.setItem('zoomLevel', zoomLevel.toString());
  }, [zoomLevel]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const setZoomLevel = (level: number) => {
    // Updated valid zoom levels - every 10% from 70% to 150%
    const validLevels = [70, 80, 90, 100, 110, 120, 130, 140, 150];
    if (validLevels.includes(level)) {
      setZoomLevelState(level);
    }
  };

  return (
    <LayoutContext.Provider value={{ 
      sidebarCollapsed, 
      toggleSidebar, 
      zoomLevel, 
      setZoomLevel 
    }}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within LayoutProvider');
  }
  return context;
};